import { useState, useEffect, useRef } from "react";
import { MailWarning, Upload, X, FileText, Code, Copy, Store, ShoppingBag, Hash, User, ScanLine, Cpu, Sparkles, MapPin, CheckCircle, AlertCircle, CheckSquare } from "lucide-react";

const MAX_USAGE = 10;

const POSITIONS = [
  "CFO / Financial Director", "Accounting Manager", "Senior Accountant",
  "Accountant", "Finance Manager", "Finance Analyst", "AP/AR Specialist",
  "Tax Specialist", "Internal Auditor", "Business Owner", "IT Manager", "Other",
];

const FREE_EMAIL_DOMAINS = new Set([
  "gmail.com","googlemail.com","yahoo.com","yahoo.co.th","yahoo.co.uk","yahoo.co.jp",
  "hotmail.com","hotmail.co.th","outlook.com","outlook.co.th","live.com","msn.com",
  "icloud.com","me.com","mac.com","aol.com","protonmail.com","proton.me",
  "tutanota.com","zoho.com","yandex.com","yandex.ru","mail.com","inbox.com",
  "gmx.com","gmx.net","fastmail.com","hey.com","mailfence.com","pm.me",
  "windowslive.com","passport.com","thaimail.com","sanook.com","truemove.com",
]);

// ── Lambda API Endpoints ──
const API_REGISTER    = "https://yg6kqqsrxr5phimfpziaitrbzq0uvqpb.lambda-url.us-east-1.on.aws/";
const API_VERIFY_OTP  = "https://xjiauf3kcviauwadr3seeyqh440bupkp.lambda-url.us-east-1.on.aws/";
const API_CHECK_QUOTA = "https://4nycqjjjcy4bykgpwqbknfkjdq0bkekr.lambda-url.us-east-1.on.aws/";
const API_ANALYZE     = "https://gtokporjwi.execute-api.us-east-1.amazonaws.com/prod/document-analysis";
const API_KEY         = "ay7ZJ7sB90acsOwtNvWMRyOx1Y3XHpX4FD0kqky3";

// ── Helpers ──
function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}
function isCorporateEmail(email) {
  const domain = email.split("@")[1]?.toLowerCase();
  if (!domain) return false;
  return !FREE_EMAIL_DOMAINS.has(domain);
}
function fmtNum(num) {
  return num ? Number(num).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0.00";
}
function getConfidenceStyles(score) {
  if (!score) return { color: "text-blue-400", bg: "bg-blue-50", border: "border-blue-200" };
  if (score >= 90) return { color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200" };
  if (score >= 70) return { color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200" };
  return { color: "text-red-600", bg: "bg-red-50", border: "border-red-200" };
}

// ── OTP Input ──
function OTPInput({ value, onChange }) {
  const inputs = useRef([]);
  const digits = value.split("");
  function handleChange(i, e) {
    const val = e.target.value.replace(/\D/g, "").slice(-1);
    const next = [...digits]; next[i] = val;
    onChange(next.join(""));
    if (val && i < 5) inputs.current[i + 1]?.focus();
  }
  function handleKeyDown(i, e) {
    if (e.key === "Backspace" && !digits[i] && i > 0) inputs.current[i - 1]?.focus();
  }
  return (
    <div className="flex justify-center gap-3 my-6">
      {[0,1,2,3,4,5].map(i => (
        <input key={i} ref={el => inputs.current[i] = el} maxLength={1}
          value={digits[i] || ""} onChange={e => handleChange(i, e)} onKeyDown={e => handleKeyDown(i, e)}
          inputMode="numeric"
          className="w-12 h-14 text-center text-2xl font-bold rounded-lg border border-blue-200 bg-white text-blue-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
        />
      ))}
    </div>
  );
}

function InputField({ label, error, ...props }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-semibold uppercase tracking-wide text-blue-700">{label}</label>
      <input className={`px-3 py-2.5 rounded-lg border bg-white text-blue-900 text-sm outline-none transition-all placeholder-blue-300
        ${error ? "border-red-400 focus:ring-1 focus:ring-red-200" : "border-blue-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"}`}
        {...props} />
      {error && <span className="text-xs text-red-500">{error}</span>}
    </div>
  );
}

function SelectField({ label, error, children, ...props }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-semibold uppercase tracking-wide text-blue-700">{label}</label>
      <select className={`px-3 py-2.5 rounded-lg border bg-white text-blue-900 text-sm outline-none transition-all appearance-none
        ${error ? "border-red-400 focus:ring-1 focus:ring-red-200" : "border-blue-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"}`}
        {...props}>
        {children}
      </select>
      {error && <span className="text-xs text-red-500">{error}</span>}
    </div>
  );
}

function StatusMsg({ type, msg, loading }) {
  const styles = { success: "bg-green-50 border-green-200 text-green-700", error: "bg-red-50 border-red-200 text-red-600", info: "bg-blue-50 border-blue-200 text-blue-600" };
  const icons  = { success: "✅", error: "⚠️", info: "ℹ️" };
  return (
    <div className={`flex items-center gap-2 px-4 py-3 rounded-lg border text-sm mb-4 ${styles[type]}`}>
      {loading ? <span className="w-4 h-4 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin flex-shrink-0" /> : <span>{icons[type]}</span>}
      {msg}
    </div>
  );
}

function PrimaryButton({ children, loading, disabled, onClick }) {
  return (
    <button onClick={onClick} disabled={loading || disabled}
      className="w-full py-3 rounded-lg bg-gradient-to-r from-blue-600 to-blue-900 text-white font-semibold text-sm tracking-wide
        hover:shadow-lg hover:shadow-blue-200 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-150
        disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none mt-2">
      {loading
        ? <span className="flex items-center justify-center gap-2">
            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            กำลังดำเนินการ...
          </span>
        : children}
    </button>
  );
}

// ── Main App ──
export default function App() {
  const [step, setStep]             = useState("register");
  const [form, setForm]             = useState({ company: "", fullname: "", phone: "", email: "", position: "" });
  const [errors, setErrors]         = useState({});
  const [otp, setOtp]               = useState("");
  const [otpTimer, setOtpTimer]     = useState(60);
  const [loading, setLoading]       = useState(false);
  const [status, setStatus]         = useState(null);
  const [usageCount, setUsageCount] = useState(0);

  // Trial / OCR state
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl]     = useState(null);
  const [result, setResult]             = useState(null);
  const [isAnalyzing, setIsAnalyzing]   = useState(false);
  const [analyzeError, setAnalyzeError] = useState(null);
  const [activeTab, setActiveTab]       = useState("simple");
  const [drag, setDrag]                 = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (step !== "otp") return;
    setOtpTimer(60);
    const id = setInterval(() => setOtpTimer(t => t > 0 ? t - 1 : 0), 1000);
    return () => clearInterval(id);
  }, [step]);

  function validateRegister() {
    const e = {};
    if (!form.company.trim())                e.company  = "กรุณากรอกชื่อบริษัท";
    if (!form.fullname.trim())               e.fullname = "กรุณากรอกชื่อ-นามสกุล";
    if (!form.phone.match(/^[0-9]{9,10}$/)) e.phone    = "เบอร์โทรศัพท์ไม่ถูกต้อง";
    if (!isValidEmail(form.email))           e.email    = "รูปแบบ Email ไม่ถูกต้อง";
    if (!form.position)                      e.position = "กรุณาเลือกตำแหน่งงาน";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleRegister() {
    if (!validateRegister()) return;
    setLoading(true); setStatus(null);
    try {
      const res = await fetch(API_REGISTER, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
      const text = await res.text();
      let data = {}; try { data = JSON.parse(text); } catch(e) {}
      if (!res.ok) throw new Error(data.error || text || "เกิดข้อผิดพลาด");
      setStep("otp");
    } catch(err) { setStatus({ type: "error", msg: err.message || "เกิดข้อผิดพลาด กรุณาลองใหม่" }); }
    setLoading(false);
  }

  async function handleVerifyOTP() {
    if (otp.length < 6) { setStatus({ type: "error", msg: "กรุณากรอก OTP ให้ครบ 6 หลัก" }); return; }
    setLoading(true); setStatus(null);
    try {
      const res = await fetch(API_VERIFY_OTP, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: form.email, otp }) });
      const text = await res.text();
      let data = {}; try { data = JSON.parse(text); } catch(e) {}
      if (!res.ok) throw new Error(data.error || "OTP ไม่ถูกต้อง");
        try {
          const qRes = await fetch(API_CHECK_QUOTA, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: form.email, check_only: true }),
          });
          const qText = await qRes.text();
          let qData = {}; try { qData = JSON.parse(qText); } catch(e) {}
          if (qData.usage_count !== undefined) setUsageCount(qData.usage_count);
        } catch(e) {}
      setStep("trial");
    } catch(err) { setStatus({ type: "error", msg: err.message || "OTP ไม่ถูกต้อง กรุณาลองใหม่" }); }
    setLoading(false);
  }

  function handleFileChange(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { setAnalyzeError("ขนาดไฟล์ใหญ่เกิน 10MB"); return; }
    if (!["image/jpeg","image/png","application/pdf"].includes(f.type)) { setAnalyzeError("รองรับเฉพาะ JPG, PNG และ PDF"); return; }
    setSelectedFile(f); setAnalyzeError(null); setResult(null);
    if (f.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (ev) => setPreviewUrl(ev.target.result);
      reader.readAsDataURL(f);
    } else { setPreviewUrl(null); }
  }

  function handleRemoveFile(e) {
    e?.stopPropagation();
    setSelectedFile(null); setPreviewUrl(null); setResult(null); setAnalyzeError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  async function handleAnalyze() {
    if (!selectedFile || usageCount >= MAX_USAGE) return;
    setIsAnalyzing(true); setAnalyzeError(null); setResult(null);
    try {
      const quotaRes = await fetch(API_CHECK_QUOTA, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: form.email }) });
      const quotaText = await quotaRes.text();
      let quotaData = {}; try { quotaData = JSON.parse(quotaText); } catch(e) {}
      if (!quotaRes.ok) throw new Error(quotaData.error || "ไม่สามารถใช้งานได้");

      const analyzeRes = await fetch(API_ANALYZE, { method: "POST", headers: { "x-api-key": API_KEY, "Content-Type": selectedFile.type }, body: selectedFile });
      const data = await analyzeRes.json();
      if (!analyzeRes.ok || data.error) throw new Error(data.error || "เกิดข้อผิดพลาดในการวิเคราะห์");
      setResult(data);
      setUsageCount(quotaData.usage_count);
    } catch(err) { setAnalyzeError(err.message || "เกิดข้อผิดพลาด กรุณาลองใหม่"); }
    setIsAnalyzing(false);
  }

  function copyToClipboard(text) { if (text) navigator.clipboard.writeText(text); }

  const quotaPct  = (usageCount / MAX_USAGE) * 100;
  const stepIndex = ["register","otp","trial"].indexOf(step);
  const steps     = ["register","otp","trial"];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-100 to-blue-100">

      {/* NAV */}
      <nav className="sticky top-0 z-50 flex items-center justify-between px-12 py-4 bg-white/80 backdrop-blur-md border-b border-blue-100 shadow-sm">
        <div className="flex items-center gap-3">
          <img src="aces-logo-black.png" alt="ACES" className="h-12" />
          <span className="text-lg font-semibold tracking-widest text-blue-800 px-3 py-1 rounded uppercase">Document Intelligence</span>
        </div>
        <span className="text-sm text-blue-400 hidden md:block">Enterprise Trial Platform</span>
      </nav>

      {/* HERO */}
      <div className="max-w-3xl mx-auto px-6 pt-16 pb-10 text-center">
        <div className="flex items-center justify-center gap-3 mb-5">
          <div className="w-8 h-px bg-blue-400" />
          <span className="text-xs font-bold tracking-widest text-blue-500 uppercase">AI-Powered Document Analysis</span>
          <div className="w-8 h-px bg-blue-400" />
        </div>
        <h1 className="text-6xl font-bold text-blue-900 leading-tight mb-5">Document Intelligence</h1>
        <p className="text-blue-600 text-lg leading-relaxed max-w-xl mx-auto">
          แปลงใบเสร็จ ใบแจ้งหนี้ และเอกสารทางการเงินทุกรูปแบบ ให้เป็นข้อมูลที่พร้อมใช้งานทันที ด้วยความแม่นยำระดับ Enterprise
        </p>
      </div>

      {/* FEATURES */}
      <div className="max-w-7xl mx-auto px-6 pb-12">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: <img src="Doc_source.svg" alt="logo" /> , title: "รองรับทุกรูปแบบ",  desc: "Invoice, ใบเสร็จ, ตั๋วทางด่วน และอื่นๆ" },
            { icon: "⚡", title: "ผลลัพธ์ทันที",      desc: "วิเคราะห์และส่งข้อมูล JSON ภายในไม่กี่วินาที" },
            { icon: "🎯", title: "แม่นยำสูง",          desc: "ระบบ confidence scoring แบบ real-time" },
            { icon: "🔒", title: "ปลอดภัย",            desc: "ข้อมูลเข้ารหัสทุกขั้นตอน ไม่มีการจัดเก็บถาวร" },
          ].map(f => (
            <div key={f.title} className="bg-white/70 border border-blue-100 rounded-xl p-5 flex flex-col items-center text-center gap-3 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
              <div className="w-11 h-11 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-2xl shadow-sm">{f.icon}</div>
              <div>
                <div className="text-sm font-semibold text-blue-900 mb-1">{f.title}</div>
                <div className="text-xs text-blue-400 leading-relaxed">{f.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* REGISTER / OTP SECTION */}
      {step !== "trial" && (
        <div className="max-w-6xl mx-auto px-6 pb-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          {/* Left info */}
          <div className="flex flex-col gap-5 items-center lg:items-start">
            <h2 className="text-2xl font-bold text-blue-900">ทดลองใช้ฟรี</h2>
            <div className="w-full sm:w-1/2 lg:w-[445px]">
              <iframe width="100%" height="250"
                src="https://www.youtube.com/embed/S9CBXHrhIUc?si=3DlZKvflYKuc1pre"
                title="YouTube video player" frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin" allowFullScreen />
            </div>
            <div className="flex flex-col gap-3 w-full sm:w-1/2 lg:w-[445px]">
              {[
                { label: "รองรับไฟล์",      value: "JPG, PNG, PDF" },
                { label: "ขนาดไฟล์สูงสุด", value: "10 MB" },
                { label: "สิทธิ์ทดลองใช้", value: "10 ครั้ง / account" },
                { label: "ผลลัพธ์",          value: "JSON พร้อมใช้งาน" },
              ].map(row => (
                <div key={row.label} className="flex items-center justify-between px-4 py-2.5 bg-white/60 border border-blue-100 rounded-lg text-sm">
                  <span className="text-blue-600">{row.label}</span>
                  <span className="font-semibold text-blue-800">{row.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Card */}
          <div className="bg-white/80 backdrop-blur-lg border border-blue-100 rounded-2xl shadow-xl shadow-blue-100 overflow-hidden">
            <div className="px-8 pt-7 pb-6 bg-gradient-to-r from-blue-50/80 to-white border-b border-blue-50">
              <div className="flex items-center gap-2 mb-4">
                {steps.map((s, i) => (
                  <div key={s} className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    i === stepIndex ? "bg-blue-500 shadow shadow-blue-300 scale-125" :
                    i <  stepIndex  ? "bg-green-400" : "bg-blue-200"
                  }`} />
                ))}
              </div>
              <h2 className="text-xl font-bold text-blue-900">
                {step === "register" ? "ลงทะเบียนทดลองใช้" : "ยืนยัน Email"}
              </h2>
              <p className="text-sm text-blue-600 mt-1">
                {step === "register" ? "กรอกข้อมูลเพื่อเริ่มทดลองใช้ฟรี 10 ครั้ง" : `กรอกรหัส 6 หลักที่ส่งไปยัง ${form.email}`}
              </p>
            </div>
            <div className="px-8 py-6">
              {step === "register" && (
                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-2 gap-3">
                    <InputField label="ชื่อ-นามสกุล *" placeholder="สมชาย ใจดี"
                      value={form.fullname} onChange={e => setForm({...form, fullname: e.target.value})} error={errors.fullname} />
                    <InputField label="เบอร์โทรศัพท์ *" placeholder="0812345678"
                      value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} error={errors.phone} />
                  </div>
                  <InputField label="ชื่อบริษัท *" placeholder="บริษัท ตัวอย่าง จำกัด"
                    value={form.company} onChange={e => setForm({...form, company: e.target.value})} error={errors.company} />
                  <InputField label="Email บริษัท *" type="email" placeholder="yourname@company.com"
                    value={form.email} onChange={e => setForm({...form, email: e.target.value})} error={errors.email} />
                  {form.email && isValidEmail(form.email) && !isCorporateEmail(form.email) && !errors.email && (
                    <p className="text-xs text-amber-500 flex items-center gap-1 mt-0.5">
                      <MailWarning className="w-5 h-5"/> กรุณาลงทะเบียนด้วย Email บริษัท
                    </p>
                  )}
                  <SelectField label="ตำแหน่งงาน *" value={form.position} onChange={e => setForm({...form, position: e.target.value})} error={errors.position}>
                    <option value="">เลือกตำแหน่ง</option>
                    {POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
                  </SelectField>
                  {status && <StatusMsg {...status} />}
                  <PrimaryButton loading={loading} onClick={handleRegister}>ลงทะเบียนและรับ OTP →</PrimaryButton>
                </div>
              )}
              {step === "otp" && (
                <div>
                  <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3 text-sm text-blue-600 leading-relaxed mb-2">
                    ส่งรหัส OTP ไปยัง <strong className="text-blue-800">{form.email}</strong> แล้ว
                    — รหัสจะหมดอายุใน <strong className="text-blue-800">10 นาที</strong>
                  </div>
                  <OTPInput value={otp} onChange={setOtp} />
                  {status && <StatusMsg {...status} />}
                  <PrimaryButton loading={loading} disabled={otp.length < 6} onClick={handleVerifyOTP}>ยืนยัน OTP →</PrimaryButton>
                  <p className="text-center text-sm text-blue-400 mt-4">
                    {otpTimer > 0 ? `ส่งรหัสใหม่ได้ใน ${otpTimer} วินาที`
                      : <><span>ไม่ได้รับรหัส? </span>
                          <button className="text-blue-600 font-semibold hover:underline"
                            onClick={() => { setOtpTimer(60); setStatus({ type: "info", msg: "ส่ง OTP ใหม่แล้ว" }); }}>
                            ส่งอีกครั้ง
                          </button>
                        </>
                    }
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* TRIAL SECTION */}
      {step === "trial" && (
        <div className="max-w-7xl mx-auto px-6 pb-20">
          {/* Trial header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-2">
              {steps.map((s, i) => (
                <div key={s} className={`w-2 h-2 rounded-full ${i <= stepIndex ? "bg-green-400" : "bg-blue-200"}`} />
              ))}
            </div>
            <h2 className="text-2xl font-bold text-blue-900">สวัสดี, {form.fullname.split(" ")[0]} 👋</h2>
            <p className="text-blue-500 text-sm mt-1">
              สิทธิ์ทดลองใช้เหลือ <strong className="text-blue-700">{MAX_USAGE - usageCount} / {MAX_USAGE} ครั้ง</strong>
            </p>
            <div className="mt-3 max-w-xs">
              <div className="h-1.5 bg-blue-100 rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-500 ${quotaPct >= 80 ? "bg-gradient-to-r from-orange-400 to-red-500" : "bg-gradient-to-r from-blue-400 to-blue-700"}`}
                  style={{width: `${quotaPct}%`}} />
              </div>
            </div>
          </div>

          {usageCount >= MAX_USAGE ? (
            <div className="text-center py-20 bg-white/60 rounded-2xl border border-blue-100">
              <div className="text-6xl mb-4">📬</div>
              <h3 className="text-xl font-bold text-blue-900 mb-2">ใช้ครบ 10 ครั้งแล้ว</h3>
              <p className="text-blue-400 leading-relaxed">ขอบคุณที่ทดลองใช้ Document Intelligence<br/>ทีมงานจะติดต่อกลับเพื่อนำเสนอแผน Enterprise</p>
            </div>
          ) : (
            <div className="grid lg:grid-cols-2 gap-8">

              {/* LEFT: Upload */}
              <div className="bg-white/80 backdrop-blur-xl border border-blue-100 rounded-2xl p-6 flex flex-col h-[680px] shadow-xl shadow-blue-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-blue-50 rounded-lg border border-blue-100">
                    <ScanLine className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-blue-900">อัปโหลดเอกสาร</h3>
                    <p className="text-xs text-blue-400 italic">JPG, PNG, PDF · สูงสุด 10 MB</p>
                  </div>
                </div>

                {/* Drop zone */}
                <div
                  onClick={() => !selectedFile && fileInputRef.current?.click()}
                  onDragOver={e => { e.preventDefault(); setDrag(true); }}
                  onDragLeave={() => setDrag(false)}
                  onDrop={e => { e.preventDefault(); setDrag(false); handleFileChange({ target: { files: e.dataTransfer.files } }); }}
                  className={`flex-1 border-2 border-dashed rounded-xl flex flex-col items-center justify-center cursor-pointer transition-all relative overflow-hidden group
                    ${selectedFile ? "border-blue-300 bg-blue-50/50" : drag ? "border-blue-500 bg-blue-50" : "border-blue-200 hover:border-blue-400 hover:bg-blue-50/50"}`}
                >
                  {selectedFile ? (
                    previewUrl
                      ? <img src={previewUrl} className="w-full h-full object-contain p-4 hover:scale-105 transition-transform duration-300" alt="Preview" />
                      : <div className="text-center">
                          <FileText className="w-16 h-16 mx-auto text-blue-300 mb-4" />
                          <p className="text-sm font-medium text-blue-700 px-4 truncate">{selectedFile.name}</p>
                          <p className="text-xs text-blue-400 font-mono mt-1">PDF DOCUMENT</p>
                        </div>
                  ) : (
                    <div className="text-center group-hover:scale-105 transition-transform duration-300">
                      <div className="w-16 h-16 mx-auto bg-blue-50 rounded-full flex items-center justify-center mb-4 border border-blue-100 group-hover:border-blue-300 group-hover:shadow-md transition-all">
                        <Upload className="w-8 h-8 text-blue-300 group-hover:text-blue-500" />
                      </div>
                      <p className="text-sm font-medium text-blue-600"><strong>คลิกเพื่อเลือกไฟล์</strong> หรือลากวางที่นี่</p>
                      <p className="text-xs text-blue-300 mt-1">JPG, PNG, PDF รองรับ</p>
                    </div>
                  )}
                  {selectedFile && (
                    <button onClick={handleRemoveFile}
                      className="absolute top-4 right-4 bg-white/90 p-2 rounded-full hover:bg-red-50 hover:text-red-500 border border-blue-100 transition-all z-20">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                  <input type="file" ref={fileInputRef} className="hidden" accept=".jpeg,.jpg,.png,.pdf" onChange={handleFileChange} />
                </div>

                <button onClick={handleAnalyze} disabled={!selectedFile || isAnalyzing}
                  className="mt-5 w-full bg-gradient-to-r from-blue-600 to-blue-900 text-white py-4 rounded-xl font-bold text-sm tracking-wide
                    hover:shadow-lg hover:shadow-blue-200 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-150
                    disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex justify-center items-center gap-3">
                  {isAnalyzing
                    ? <><Cpu className="w-5 h-5 animate-spin" /><span>กำลังวิเคราะห์...</span></>
                    : <><Sparkles className="w-5 h-5" /><span>วิเคราะห์เอกสาร (เหลือ {MAX_USAGE - usageCount} ครั้ง) →</span></>
                  }
                </button>
                {analyzeError && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 text-red-600 text-xs text-center rounded-lg">{analyzeError}</div>
                )}
              </div>

              {/* RIGHT: Result */}
              <div className="bg-white/80 backdrop-blur-xl border border-blue-100 rounded-2xl flex flex-col h-[680px] shadow-xl shadow-blue-100 overflow-hidden relative">
                {isAnalyzing && (
                  <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-white/90 backdrop-blur-sm rounded-2xl">
                    <div className="relative w-20 h-20">
                      <div className="absolute inset-0 border-4 border-blue-100 rounded-full" />
                      <div className="absolute inset-0 border-4 border-t-blue-500 border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin" />
                    </div>
                    <p className="mt-5 text-blue-500 font-medium tracking-widest animate-pulse text-sm uppercase">กำลังวิเคราะห์เอกสาร...</p>
                  </div>
                )}

                {!result ? (
                  <div className="flex-1 flex flex-col items-center justify-center">
                    <Cpu className="w-20 h-20 mb-5 text-blue-100" />
                    <p className="text-sm font-mono uppercase tracking-widest text-blue-200">Waiting for data</p>
                  </div>
                ) : (
                  <>
                    {/* Tabs */}
                    <div className="flex border-b border-blue-100 bg-blue-50/50">
                      {["simple","json"].map(tab => (
                        <button key={tab} onClick={() => setActiveTab(tab)}
                          className={`flex-1 py-4 text-xs font-bold uppercase tracking-widest transition-all relative
                            ${activeTab === tab ? "text-blue-700" : "text-blue-300 hover:text-blue-500"}`}>
                          <span className="flex items-center justify-center gap-2">
                            {tab === "simple" ? <FileText className="w-4 h-4" /> : <Code className="w-4 h-4" />}
                            {tab === "simple" ? "ผลลัพธ์" : "JSON"}
                          </span>
                          {activeTab === tab && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-500" />}
                        </button>
                      ))}
                    </div>

                    <div className="flex-1 overflow-y-auto">
                      {activeTab === "simple" && (
                        <div className="p-6 space-y-5">
                          {/* Vendor card */}
                          <div className="bg-blue-50/50 p-5 rounded-xl border border-blue-100">
                            <div className="flex justify-between items-start mb-4">
                              <div>
                                <p className="text-[10px] text-blue-400 font-bold uppercase tracking-widest mb-1 flex items-center gap-1"><Store className="w-3 h-3" /> Vendor</p>
                                <h3 className="text-xl font-bold text-blue-900 font-mono">{result.vendor_name || "UNKNOWN"}</h3>
                                {result.vendor_address && (
                                  <p className="text-xs text-blue-500 mt-1 flex items-start gap-1 max-w-xs leading-relaxed">
                                    <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />{result.vendor_address}
                                  </p>
                                )}
                                {result.tax_id && (
                                  <div onClick={() => copyToClipboard(result.tax_id)}
                                    className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 bg-white rounded border border-blue-100 cursor-pointer hover:border-blue-300 transition-colors group">
                                    <Hash className="w-3 h-3 text-blue-300 group-hover:text-blue-500" />
                                    <span className="text-xs font-mono text-blue-600">{result.tax_id}</span>
                                    <Copy className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity text-blue-400" />
                                  </div>
                                )}
                              </div>
                              <div className="text-right flex flex-col items-end gap-2">
                                {result.c !== undefined && (() => {
                                  const cs = getConfidenceStyles(result.c);
                                  return (
                                    <div className={`px-3 py-1 rounded-full border ${cs.border} ${cs.bg} flex items-center gap-1.5`}>
                                      <div className={`w-1.5 h-1.5 rounded-full ${cs.color.replace("text-","bg-")}`} />
                                      <span className={`text-[10px] font-bold uppercase tracking-wide ${cs.color}`}>{result.c}% Confidence</span>
                                    </div>
                                  );
                                })()}
                                <div className="px-3 py-2 bg-white rounded-lg border border-blue-100">
                                  <p className="text-[10px] text-blue-400 font-bold uppercase tracking-widest mb-0.5">Date</p>
                                  <p className="text-sm font-mono text-blue-700">{result.date || "--/--/----"}</p>
                                </div>
                              </div>
                            </div>
                            {result.doc_name && <p className="text-[10px] text-blue-500 font-bold uppercase tracking-widest">{result.doc_name}</p>}
                            {result.customer_name && (
                              <div className="pt-3 border-t border-blue-100 flex items-center gap-2 mt-3">
                                <User className="w-4 h-4 text-blue-300" />
                                <div>
                                  <p className="text-[10px] text-blue-400 font-bold uppercase tracking-widest">Bill To</p>
                                  <p className="text-sm text-blue-700">{result.customer_name}</p>
                                </div>
                              </div>
                            )}
                            <div className={`mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[10px] uppercase tracking-wider font-bold ${
                              result.is_signed ? "bg-emerald-50 border-emerald-200 text-emerald-600" : "bg-amber-50 border-amber-200 text-amber-600"
                            }`}>
                              {result.is_signed ? <><CheckCircle className="w-3 h-3" /> Signature Verified</> : <><AlertCircle className="w-3 h-3" /> Missing Signature</>}
                            </div>
                          </div>

                          {/* Selected marks */}
                          {result.selected_marks?.length > 0 && (
                            <div>
                              <p className="text-[10px] text-blue-400 font-bold uppercase tracking-widest mb-2 flex items-center gap-1"><CheckSquare className="w-3 h-3" /> Selected Options</p>
                              <div className="space-y-2">
                                {result.selected_marks.map((mark, idx) => (
                                  <div key={idx} className="flex items-center gap-3 px-4 py-2.5 bg-blue-50 border border-blue-100 rounded-lg">
                                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                                    <span className="text-sm text-blue-700 font-medium">{mark}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Items table */}
                          <div>
                            <p className="text-[10px] text-blue-400 font-bold uppercase tracking-widest mb-2 flex items-center gap-1"><ShoppingBag className="w-3 h-3" /> Items List</p>
                            <div className="border border-blue-100 rounded-xl overflow-hidden">
                              <table className="w-full text-sm">
                                <thead className="bg-blue-50 text-blue-400 border-b border-blue-100">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-mono">Description</th>
                                    <th className="px-4 py-3 text-right text-xs uppercase tracking-wider font-mono w-16">{result.type === "Toll" ? "" : "Qty"}</th>
                                    <th className="px-4 py-3 text-right text-xs uppercase tracking-wider font-mono w-28">{result.type === "Toll" ? "" : "Price"}</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-blue-50">
                                  {result.items?.map((item, idx) => (
                                    <tr key={idx} className="hover:bg-blue-50/50 transition-colors">
                                      <td className="px-4 py-3 text-blue-700 font-medium">{item.name}</td>
                                      <td className="px-4 py-3 text-right text-blue-400 font-mono">{result.type === "Toll" ? "" : item.quantity}</td>
                                      <td className="px-4 py-3 text-right text-emerald-600 font-mono">{result.type === "Toll" ? "" : fmtNum(item.price)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                                <tfoot className="bg-blue-50 border-t border-blue-100">
                                  {result.subtotal > 0 && (
                                    <tr>
                                      <td colSpan="2" className="px-4 py-2 text-right text-xs text-blue-400 uppercase tracking-wider font-mono">Subtotal</td>
                                      <td className="px-4 py-2 text-right text-blue-600 font-mono">{fmtNum(result.subtotal)}</td>
                                    </tr>
                                  )}
                                  <tr>
                                    <td colSpan="2" className="px-4 py-2 text-right text-xs text-blue-400 uppercase tracking-wider font-mono">VAT</td>
                                    <td className="px-4 py-2 text-right text-blue-600 font-mono">{fmtNum(result.vat_amount)}</td>
                                  </tr>
                                  {result.discount > 0 && (
                                    <tr>
                                      <td colSpan="2" className="px-4 py-2 text-right text-xs text-blue-400 uppercase tracking-wider font-mono">Discount</td>
                                      <td className="px-4 py-2 text-right text-blue-600 font-mono">{fmtNum(result.discount)}</td>
                                    </tr>
                                  )}
                                  <tr>
                                    <td colSpan="2" className="px-4 py-5 text-right text-sm text-blue-700 font-bold uppercase tracking-wider font-mono">Total Amount</td>
                                    <td className="px-4 py-5 text-right text-2xl font-bold text-blue-900 font-mono">{fmtNum(result.total_amount)}</td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          </div>

                          {/* Token usage */}
                          {result._usage_stats && (
                            <div className="p-5 bg-blue-50/50 rounded-xl border border-blue-100">
                              <div className="flex items-center gap-2 mb-4">
                                <Cpu className="w-4 h-4 text-blue-400" />
                                <p className="text-[10px] text-blue-400 font-bold uppercase tracking-widest font-mono">Resource Consumption</p>
                              </div>
                              <div className="grid grid-cols-3 gap-6">
                                <div>
                                  <p className="text-[10px] text-blue-400 uppercase font-mono mb-1">Input</p>
                                  <p className="text-lg font-bold text-blue-900 font-mono">{result._usage_stats.prompt_tokens}<span className="text-[10px] text-blue-300 ml-1">TK</span></p>
                                </div>
                                <div>
                                  <p className="text-[10px] text-blue-400 uppercase font-mono mb-1">Output</p>
                                  <p className="text-lg font-bold text-blue-900 font-mono">{result._usage_stats.output_tokens}<span className="text-[10px] text-blue-300 ml-1">TK</span></p>
                                </div>
                                <div>
                                  <p className="text-[10px] text-blue-400 uppercase font-mono mb-1">Unit Cost</p>
                                  <p className="text-lg font-bold text-emerald-600 font-mono">${result._usage_stats.estimated_cost_usd.toFixed(5)}</p>
                                </div>
                              </div>
                              <div className="mt-4 h-1.5 bg-blue-100 rounded-full overflow-hidden flex">
                                <div className="h-full bg-blue-400 transition-all duration-700" style={{width:`${(result._usage_stats.prompt_tokens/result._usage_stats.total_tokens)*100}%`}} />
                                <div className="h-full bg-emerald-400 transition-all duration-700" style={{width:`${(result._usage_stats.output_tokens/result._usage_stats.total_tokens)*100}%`}} />
                              </div>
                              <p className="text-[9px] text-blue-300 font-mono uppercase mt-1">Token Distribution</p>
                            </div>
                          )}
                        </div>
                      )}

                      {activeTab === "json" && (
                        <div className="relative h-full bg-slate-950/5 p-5">
                          <button onClick={() => copyToClipboard(JSON.stringify(result, null, 2))}
                            className="absolute top-5 right-5 text-xs bg-white hover:bg-blue-600 hover:text-white text-blue-600 px-3 py-1.5 rounded-lg border border-blue-100 transition-colors z-10 flex items-center gap-1.5">
                            <Copy className="w-3 h-3" /> COPY JSON
                          </button>
                          <pre className="font-mono text-[11px] text-blue-700 leading-relaxed overflow-auto h-full p-2">
                            {JSON.stringify(result, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* FOOTER */}
      <footer className="text-center pb-8 pt-6 text-xs text-blue-300 border-t border-blue-100">
        © 2569 ACES · Document Intelligence Platform · ข้อมูลทั้งหมดถูกเข้ารหัสและไม่มีการจัดเก็บถาวร
      </footer>
    </div>
  );
}